import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  ChevronRight,
  ChevronDown,
  Sparkles,
  PenLine,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { jobUrlSegment, toSlug } from '../../lib/slug';
import type { Job, Invitation, ScriptDraft } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Textarea from '../../components/ui/Textarea';
import Modal from '../../components/ui/Modal';
import { AIAssistantShell, type AIAssistantField } from '../../components/ai';

const QK_SCRIPTS_PAGE = ['agency', 'scripts-page'] as const;
const qkJobs = [...QK_SCRIPTS_PAGE, 'jobs'] as const;
const qkOverview = (sortedJobIds: string) => [...QK_SCRIPTS_PAGE, 'overview', sortedJobIds] as const;

const scriptAIFields: AIAssistantField[] = [
  { key: 'mustHaveUsps', label: 'Must-have product USPs', type: 'textarea', placeholder: 'Key product features that MUST be mentioned...' },
  { key: 'keyTalkingPoints', label: 'Key talking points', type: 'textarea', placeholder: 'Main points the KOL should cover...' },
  { key: 'tone', label: 'Tone', type: 'select', options: [
    { value: 'friendly', label: 'Friendly & conversational' },
    { value: 'professional', label: 'Professional' },
    { value: 'humorous', label: 'Humorous' },
    { value: 'educational', label: 'Educational' },
    { value: 'inspirational', label: 'Inspirational' },
  ]},
  { key: 'platformFocus', label: 'Platform', type: 'select', options: [
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'youtube', label: 'YouTube' },
  ]},
  { key: 'language', label: 'Language', type: 'select', options: [
    { value: 'English', label: 'English' },
    { value: 'Malay', label: 'Bahasa Melayu' },
    { value: 'Mandarin', label: 'Mandarin' },
  ], defaultValue: 'English' },
  { key: 'durationSeconds', label: 'Video duration (seconds)', type: 'number', placeholder: '60', defaultValue: '60' },
  { key: 'brandVoice', label: 'Brand voice', type: 'text', placeholder: 'e.g., Modern and youthful' },
  { key: 'avoidPhrases', label: 'Phrases to avoid', type: 'text', placeholder: 'e.g., cheap, discount' },
  { key: 'ctaPreference', label: 'Preferred call-to-action', type: 'text', placeholder: 'e.g., Visit our website, Use code X' },
];

type ScriptStatusLabel = 'DRAFT' | 'SUBMITTED_FOR_BRAND' | 'APPROVED_BY_BRAND' | 'REVISION_REQUESTED';

interface CampaignRow {
  job: Job;
  invitations: Invitation[];
  scripts: ScriptDraft[];
  needsAction: boolean;
  actionCount: number;
  approvedCount: number;
  awaitingApproval: number;
  pendingReview: number;
  revisionNeeded: number;
  totalKols: number;
}

function scriptsForInvitation(scripts: ScriptDraft[], invitationId: string) {
  return scripts
    .filter((s) => s.invitationId === invitationId)
    .sort((a, b) => (b.version || 0) - (a.version || 0));
}

function statusBadge(status: ScriptStatusLabel) {
  switch (status) {
    case 'APPROVED_BY_BRAND':
      return <Badge variant="success" className="text-xs">Approved</Badge>;
    case 'REVISION_REQUESTED':
      return <Badge variant="destructive" className="text-xs">Revision needed</Badge>;
    case 'SUBMITTED_FOR_BRAND':
      return <Badge variant="default" className="text-xs">Under review</Badge>;
    default:
      return <Badge variant="secondary" className="text-xs">Draft v{status}</Badge>;
  }
}

export default function AgencyScripts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  const jobsQuery = useQuery({
    queryKey: qkJobs,
    queryFn: async () => {
      const res = await api.get<Job[]>('/jobs');
      return res.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });

  const jobs = jobsQuery.data ?? [];
  const sortedJobIds = useMemo(() => [...jobs.map((j) => j.id)].sort().join(','), [jobs]);

  const overviewQuery = useQuery({
    queryKey: qkOverview(sortedJobIds),
    enabled: jobs.length > 0,
    queryFn: async () => {
      const jobIdsParam = jobs.map((j) => j.id).join(',');
      const [invRes, scrRes] = await Promise.all([
        api.get<Invitation[]>('/invitations', { params: { jobIds: jobIdsParam } }),
        api.get<ScriptDraft[]>('/scripts', { params: { jobIds: jobIdsParam, omitContent: 'true' } }),
      ]);
      const invFlat = invRes.data || [];
      const scrFlat = scrRes.data || [];
      const invMap: Record<string, Invitation[]> = {};
      const scrMap: Record<string, ScriptDraft[]> = {};
      for (const j of jobs) {
        invMap[j.id] = [];
        scrMap[j.id] = [];
      }
      for (const inv of invFlat) {
        if (inv.jobId && invMap[inv.jobId]) invMap[inv.jobId].push(inv);
      }
      for (const s of scrFlat) {
        if (s.jobId && scrMap[s.jobId]) scrMap[s.jobId].push(s);
      }
      return { invMap, scrMap };
    },
    staleTime: 10 * 60 * 1000,
  });

  const allInvitations = overviewQuery.data?.invMap ?? {};
  const allScripts = overviewQuery.data?.scrMap ?? {};

  const [showScriptAI, setShowScriptAI] = useState(false);
  const [scriptAITarget, setScriptAITarget] = useState<{ jobId: string; invitationId: string; kolName: string } | null>(null);
  const [scriptAIGenerating, setScriptAIGenerating] = useState(false);
  const [scriptAIError, setScriptAIError] = useState<string | null>(null);
  const [scriptAIResult, setScriptAIResult] = useState<string | null>(null);
  const [scriptAISaving, setScriptAISaving] = useState(false);

  const [showManualScript, setShowManualScript] = useState(false);
  const [manualScriptTarget, setManualScriptTarget] = useState<{ jobId: string; invitationId: string; kolName: string } | null>(null);
  const [manualScriptContent, setManualScriptContent] = useState('');
  const [manualScriptSaving, setManualScriptSaving] = useState(false);

  const loadingJobs = jobsQuery.isPending;
  const loadingDetails = jobs.length > 0 && (overviewQuery.isPending || overviewQuery.isFetching);
  const refetching = jobsQuery.isFetching || overviewQuery.isFetching;

  const refreshJob = useCallback(
    async (jobId: string) => {
      try {
        const [invRes, scrRes] = await Promise.all([
          api.get('/invitations', { params: { jobId } }),
          api.get('/scripts', { params: { jobId, omitContent: 'true' } }),
        ]);
        queryClient.setQueryData(qkOverview(sortedJobIds), (old: { invMap: Record<string, Invitation[]>; scrMap: Record<string, ScriptDraft[]> } | undefined) => {
          if (!old) return old;
          return {
            invMap: { ...old.invMap, [jobId]: invRes.data || [] },
            scrMap: { ...old.scrMap, [jobId]: scrRes.data || [] },
          };
        });
      } catch (e) {
        console.error(e);
      }
    },
    [queryClient, sortedJobIds]
  );

  const fullReload = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: QK_SCRIPTS_PAGE });
  }, [queryClient]);

  const campaignRows = useMemo<CampaignRow[]>(() => {
    return jobs.map((job) => {
      const invitations = (allInvitations[job.id] || []).filter((inv) => inv.isApprovedForProduction || inv.isShortlisted);
      const scripts = allScripts[job.id] || [];

      let approvedCount = 0;
      let awaitingApproval = 0;
      let revisionNeeded = 0;
      let pendingReview = 0;

      for (const inv of invitations) {
        const latest = scriptsForInvitation(scripts, inv.id)[0];
        if (inv.isApprovedForProduction) {
          approvedCount++;
          if (latest?.status === 'REVISION_REQUESTED') revisionNeeded++;
          else if (latest?.status === 'SUBMITTED_FOR_BRAND') pendingReview++;
        } else {
          awaitingApproval++;
        }
      }

      const actionCount = revisionNeeded + approvedCount - (pendingReview + scripts.filter((s) => s.status === 'APPROVED_BY_BRAND').length);

      return {
        job,
        invitations,
        scripts,
        needsAction: revisionNeeded > 0 || (approvedCount > 0 && scripts.length === 0),
        actionCount: Math.max(0, actionCount),
        approvedCount,
        awaitingApproval,
        pendingReview,
        revisionNeeded,
        totalKols: invitations.length,
      };
    });
  }, [jobs, allInvitations, allScripts]);

  const { actionable, waiting } = useMemo(() => {
    const actionable: CampaignRow[] = [];
    const waiting: CampaignRow[] = [];
    for (const row of campaignRows) {
      if (row.totalKols === 0) {
        waiting.push(row);
      } else if (row.needsAction || row.approvedCount > 0) {
        actionable.push(row);
      } else {
        waiting.push(row);
      }
    }
    actionable.sort((a, b) => b.revisionNeeded - a.revisionNeeded || b.actionCount - a.actionCount);
    return { actionable, waiting };
  }, [campaignRows]);

  useEffect(() => {
    if (actionable.length > 0) {
      setExpandedJobs(new Set(actionable.map((r) => r.job.id)));
    }
  }, [actionable.length]);

  const toggleExpand = (jobId: string) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const openStudio = (jobTitle: string, scriptId: string) => {
    navigate(`/agency/scripts/${toSlug(jobTitle || 'script', scriptId)}`);
  };

  const handleScriptAIGenerate = async (values: Record<string, string>) => {
    if (!scriptAITarget) return;
    try {
      setScriptAIGenerating(true);
      setScriptAIError(null);
      setScriptAIResult(null);
      const res = await api.post('/ai/scripts/generate', {
        jobId: scriptAITarget.jobId,
        invitationId: scriptAITarget.invitationId,
        ...values,
        durationSeconds: values.durationSeconds ? parseInt(values.durationSeconds, 10) : 60,
      });
      setScriptAIResult(res.data.scriptText || JSON.stringify(res.data.generated, null, 2));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setScriptAIError(err.response?.data?.error || 'AI generation failed');
    } finally {
      setScriptAIGenerating(false);
    }
  };

  const handleScriptAIApply = async () => {
    if (!scriptAIResult || !scriptAITarget) return;
    try {
      setScriptAISaving(true);
      await api.post('/scripts', {
        jobId: scriptAITarget.jobId,
        invitationId: scriptAITarget.invitationId,
        content: scriptAIResult,
      });
      await refreshJob(scriptAITarget.jobId);
      setShowScriptAI(false);
      setScriptAIResult(null);
      setScriptAITarget(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setScriptAIError(err.response?.data?.error || 'Failed to save script');
    } finally {
      setScriptAISaving(false);
    }
  };

  const handleManualScriptSave = async () => {
    if (!manualScriptTarget) return;
    const text = manualScriptContent.trim();
    if (!text) { alert('Enter script text before saving.'); return; }
    try {
      setManualScriptSaving(true);
      await api.post('/scripts', {
        jobId: manualScriptTarget.jobId,
        invitationId: manualScriptTarget.invitationId,
        content: text,
      });
      await refreshJob(manualScriptTarget.jobId);
      setShowManualScript(false);
      setManualScriptTarget(null);
      setManualScriptContent('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Failed to save script');
    } finally {
      setManualScriptSaving(false);
    }
  };

  if (loadingJobs) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-glass-text-3" />
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader onReload={fullReload} reloading={refetching} />
        <div className="py-20 text-center text-glass-text-3">
          <FileText className="mx-auto mb-3 h-12 w-12 opacity-40" />
          <p className="text-lg text-glass-text">No campaigns yet</p>
          <p className="mt-1 text-sm">Create a campaign to start drafting scripts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader onReload={fullReload} reloading={refetching} />

      {loadingDetails && (
        <div className="flex items-center gap-2 rounded-xl border border-accent2/30 bg-accent2/10 px-4 py-3 text-sm text-accent2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading script data across campaigns…
        </div>
      )}

      {actionable.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-300" />
            <h2 className="text-lg font-semibold text-glass-text">
              Needs your attention ({actionable.length})
            </h2>
          </div>
          <div className="space-y-3">
            {actionable.map((row) => (
              <CampaignCard
                key={row.job.id}
                row={row}
                expanded={expandedJobs.has(row.job.id)}
                onToggle={() => toggleExpand(row.job.id)}
                onOpenStudio={openStudio}
                onAIDraft={(invId, kolName) => {
                  setScriptAITarget({ jobId: row.job.id, invitationId: invId, kolName });
                  setScriptAIResult(null);
                  setScriptAIError(null);
                  setShowScriptAI(true);
                }}
                onManualDraft={(invId, kolName, existingContent) => {
                  setManualScriptTarget({ jobId: row.job.id, invitationId: invId, kolName });
                  setManualScriptContent(existingContent);
                  setShowManualScript(true);
                }}
                navigate={navigate}
              />
            ))}
          </div>
        </section>
      )}

      {waiting.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5 text-glass-text-3" />
            <h2 className="text-lg font-semibold text-glass-text">
              Other campaigns ({waiting.length})
            </h2>
          </div>
          <div className="space-y-2">
            {waiting.map((row) => (
              <CampaignCard
                key={row.job.id}
                row={row}
                expanded={expandedJobs.has(row.job.id)}
                onToggle={() => toggleExpand(row.job.id)}
                onOpenStudio={openStudio}
                onAIDraft={(invId, kolName) => {
                  setScriptAITarget({ jobId: row.job.id, invitationId: invId, kolName });
                  setScriptAIResult(null);
                  setScriptAIError(null);
                  setShowScriptAI(true);
                }}
                onManualDraft={(invId, kolName, existingContent) => {
                  setManualScriptTarget({ jobId: row.job.id, invitationId: invId, kolName });
                  setManualScriptContent(existingContent);
                  setShowManualScript(true);
                }}
                navigate={navigate}
              />
            ))}
          </div>
        </section>
      )}

      {showScriptAI && (
        <AIAssistantShell
          open={showScriptAI}
          onClose={() => { setShowScriptAI(false); setScriptAITarget(null); setScriptAIResult(null); }}
          title={`Generate Script for ${scriptAITarget?.kolName || 'KOL'}`}
          description="Provide details to generate an AI-powered script draft"
          fields={scriptAIFields}
          onGenerate={handleScriptAIGenerate}
          generating={scriptAIGenerating}
          error={scriptAIError}
          result={scriptAIResult ? <pre className="whitespace-pre-wrap text-sm text-glass-text">{scriptAIResult}</pre> : undefined}
          applyLabel="Save as Draft"
          onApply={handleScriptAIApply}
          applying={scriptAISaving}
        />
      )}

      <Modal
        isOpen={showManualScript && !!manualScriptTarget}
        onClose={() => { setShowManualScript(false); setManualScriptTarget(null); setManualScriptContent(''); }}
        title={manualScriptTarget ? `Manual script — ${manualScriptTarget.kolName}` : 'Manual script'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setShowManualScript(false); setManualScriptTarget(null); setManualScriptContent(''); }} disabled={manualScriptSaving}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleManualScriptSave()} disabled={manualScriptSaving}>
              {manualScriptSaving ? 'Saving…' : 'Save as draft'}
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-glass-text-2">Paste or write the full script. This creates a new draft version.</p>
        <Textarea rows={16} className="w-full font-mono text-sm" value={manualScriptContent} onChange={(e) => setManualScriptContent(e.target.value)} placeholder="Hook, talking points, CTA, captions…" />
      </Modal>
    </div>
  );
}

function PageHeader({ onReload, reloading }: { onReload: () => void; reloading: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 md:gap-4">
      <div className="min-w-0">
        <h1 className="text-[1.375rem] font-semibold tracking-tightest text-glass-text md:text-[2.25rem]">Scripts</h1>
        <p className="mt-1 text-xs text-glass-text-2 md:text-sm">
          All campaigns at a glance. Campaigns that need script work are expanded — others are collapsed to save space.
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-11 w-11 shrink-0 gap-2 md:h-9 md:w-auto md:px-3"
        onClick={onReload}
        disabled={reloading}
        title="Fetch latest jobs and script summaries from the server"
        aria-label="Reload script data"
      >
        <RefreshCw className={cn('h-4 w-4', reloading && 'animate-spin')} />
        <span className="hidden md:inline">Reload data</span>
      </Button>
    </div>
  );
}

interface CampaignCardProps {
  row: CampaignRow;
  expanded: boolean;
  onToggle: () => void;
  onOpenStudio: (jobTitle: string, scriptId: string) => void;
  onAIDraft: (invitationId: string, kolName: string) => void;
  onManualDraft: (invitationId: string, kolName: string, existingContent: string) => void;
  navigate: ReturnType<typeof useNavigate>;
}

function CampaignCard({ row, expanded, onToggle, onOpenStudio, onAIDraft, onManualDraft, navigate }: CampaignCardProps) {
  const { job, invitations, scripts, approvedCount, awaitingApproval, pendingReview, revisionNeeded, totalKols } = row;

  const allScriptsApproved = invitations.length > 0 && invitations.every((inv) => {
    const latest = scriptsForInvitation(scripts, inv.id)[0];
    return latest?.status === 'APPROVED_BY_BRAND';
  });

  const renderStatusIndicators = (className: string) => (
    <div className={cn('items-center gap-2', className)}>
      {revisionNeeded > 0 && (
        <span className="flex items-center gap-1 rounded-full bg-rose-500/15 px-2.5 py-0.5 text-xs font-medium text-rose-500 dark:text-rose-300 border border-rose-500/30">
          <AlertCircle className="h-3 w-3" />
          {revisionNeeded} revision{revisionNeeded > 1 ? 's' : ''}
        </span>
      )}
      {pendingReview > 0 && (
        <span className="flex items-center gap-1 rounded-full bg-accent2/15 px-2.5 py-0.5 text-xs font-medium text-accentText border border-accent2/30">
          <Clock className="h-3 w-3" />
          {pendingReview} under review
        </span>
      )}
      {allScriptsApproved && totalKols > 0 && (
        <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-medium text-emerald-500 dark:text-emerald-300 border border-emerald-500/30">
          <CheckCircle2 className="h-3 w-3" />
          All done
        </span>
      )}
      {totalKols === 0 && (
        <span className="text-xs text-glass-text-3">No eligible KOLs</span>
      )}
    </div>
  );

  return (
    <div className="glass-card overflow-hidden transition-shadow hover:shadow-lg">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer flex-wrap items-start gap-2 px-[13px] py-3 text-left transition-colors hover:bg-glass-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/50 md:flex-nowrap md:items-center md:gap-3 md:px-5 md:py-4"
      >
        <div className="mt-0.5 shrink-0 text-glass-text-3 md:mt-0">
          {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-base font-semibold text-glass-text">
              {job.title}
            </span>
            <Badge variant="secondary" className="text-[10px]">{job.status}</Badge>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-glass-text-3">
            {totalKols > 0 && <span>{totalKols} KOL{totalKols !== 1 ? 's' : ''}</span>}
            {approvedCount > 0 && <span className="text-emerald-500 dark:text-emerald-300">{approvedCount} production-ready</span>}
            {awaitingApproval > 0 && <span className="text-amber-500 dark:text-amber-300">{awaitingApproval} awaiting approval</span>}
            {scripts.length > 0 && <span>{scripts.length} script draft{scripts.length !== 1 ? 's' : ''}</span>}
          </div>
        </div>

        {renderStatusIndicators('hidden shrink-0 md:flex')}
        {renderStatusIndicators('ml-7 flex basis-full flex-wrap md:hidden')}
      </button>

      {expanded && (
        <div className="border-t border-glass px-[13px] py-3 md:px-5 md:py-4">
          {totalKols === 0 ? (
            <p className="py-6 text-center text-sm text-glass-text-3">
              Shortlist KOLs in the job first, then return here to draft scripts.
            </p>
          ) : (
            <div className="-mx-[13px] divide-y divide-glass border-y border-glass md:mx-0 md:rounded-xl md:border">
              {invitations.map((inv) => {
                const kolScripts = scriptsForInvitation(scripts, inv.id);
                const latest = kolScripts[0];
                const canWrite = inv.isApprovedForProduction;

                return (
                  <div key={inv.id} className="px-[13px] py-3 md:p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <span className="font-medium text-glass-text">
                          {inv.kolProfile?.name || 'Unknown KOL'}
                        </span>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {canWrite ? (
                            <Badge variant="success" className="text-xs">Production approved</Badge>
                          ) : (
                            <Badge variant="warning" className="max-w-full whitespace-normal text-left text-xs">
                              Awaiting corporate approval (shortlisted)
                            </Badge>
                          )}
                          {latest && statusBadge(latest.status as ScriptStatusLabel)}
                          {kolScripts.length > 0 && (
                            <span className="text-xs text-glass-text-3">{kolScripts.length} ver.</span>
                          )}
                        </div>
                        {!canWrite && (
                          <p className="mt-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-2 py-1.5 text-xs text-amber-500 dark:text-amber-300">
                            Script tools unlock after corporate approves this KOL for production.
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {latest && (
                          <Button
                            type="button"
                            size="sm"
                            className="h-11 md:h-9"
                            onClick={() => onOpenStudio(job.title, latest.id)}
                          >
                            Script Studio
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm" disabled={!canWrite}
                          onClick={() => onAIDraft(inv.id, inv.kolProfile?.name || 'KOL')}
                          className="h-11 w-11 gap-1 p-0 md:h-9 md:w-auto md:px-3"
                          title={canWrite ? 'Generate AI draft' : 'Requires production approval'}
                          aria-label={`Generate AI script for ${inv.kolProfile?.name || 'KOL'}`}
                        >
                          <Sparkles className="h-4 w-4 md:h-3 md:w-3" />
                          <span className="hidden md:inline">AI</span>
                        </Button>
                        <Button
                          variant="outline" size="sm" disabled={!canWrite}
                          onClick={() => onManualDraft(inv.id, inv.kolProfile?.name || 'KOL', latest?.content || '')}
                          className="h-11 w-11 gap-1 p-0 md:h-9 md:w-auto md:px-3"
                          title={canWrite ? 'Write or paste a script' : 'Requires production approval'}
                          aria-label={`Write a manual script for ${inv.kolProfile?.name || 'KOL'}`}
                        >
                          <PenLine className="h-4 w-4 md:h-3 md:w-3" />
                          <span className="hidden md:inline">Manual</span>
                        </Button>
                      </div>
                    </div>

                    {latest && (
                      <div className="mt-3 rounded-xl bg-glass-2 border border-glass p-3">
                        <p className="line-clamp-2 whitespace-pre-wrap text-sm text-glass-text-2">
                          {(latest as ScriptDraft & { content?: string }).content?.trim()
                            ? (latest as ScriptDraft & { content?: string }).content
                            : 'Open Script Studio to view the full script.'}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-3 flex items-center justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 gap-1 text-xs md:h-9"
              onClick={() => navigate(`/agency/campaigns/${jobUrlSegment(job)}?tab=scripts`)}
            >
              Open in job <ExternalLink className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
